import React, { useEffect, useState } from "react";
import { withStyles } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import { postChallengeMealLog } from "../../../../apis/challengeService";
import Snackbar from "../../../common/Snackbar";
import Properties from "../../../../properties/Properties";
import dinnerIcon from "../../../../assets/svg-icons/dinner.svg";
import breakfastIcon from "../../../../assets/svg-icons/breakfast.svg";
import lunchIcon from "../../../../assets/svg-icons/lunch.svg";
import miniMeal from "../../../../assets/svg-icons/minimeal.svg";

function MealResponseComponent({
  classes,
  perDayViewData,
  challengeId,
  style,
  viewDate,
  runnerDetails,
  initialLoad,
}) {
  const perDayMealData = perDayViewData?.dietPlan;

  // 🔹 State for dynamic meal popups and items
  const [mealDialogs, setMealDialogs] = useState({});
  const [mealItems, setMealItems] = useState({});

  // 🔹 Convert a meal map (or mealOptions) into an array of items
  const parseMealMap = (map) =>
    Object.entries(map || {}).map(([key, value]) => ({
      name: key.replace(/_/g, " "),
      key,
      quantity: parseFloat(value) || 0,
    }));

  // 🔹 Initialize all meal types dynamically
  useEffect(() => {
    if (!perDayMealData) return;

    const meals = {};

    // Predefined meals
    if (perDayMealData.breakfastMap)
      meals["Breakfast"] = parseMealMap(perDayMealData.breakfastMap);
    if (perDayMealData.lunchMap)
      meals["Lunch"] = parseMealMap(perDayMealData.lunchMap);
    if (perDayMealData.dinnerMap)
      meals["Dinner"] = parseMealMap(perDayMealData.dinnerMap);

    // Dynamic meals (from otherMealList)
    if (Array.isArray(perDayMealData.otherMealList)) {
      perDayMealData.otherMealList.forEach((m) => {
        if (m.mealType && m.mealOptions) {
          meals[m.mealType] = parseMealMap(m.mealOptions);
        }
      });
    }

    setMealItems(meals);

    // Initialize dialog open states
    const dialogState = Object.keys(meals).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setMealDialogs(dialogState);
  }, [perDayMealData]);

  // 🔹 Handle + / - quantity changes
  const handleQuantityChange = (mealType, index, delta) => {
    setMealItems((prev) => ({
      ...prev,
      [mealType]: prev[mealType].map((item, i) =>
        i === index
          ? { ...item, quantity: Math.max(0, item.quantity + delta) }
          : item
      ),
    }));
  };

  // 🔹 Submit handler for each meal
  const handleSubmit = (mealType) => {
    const items = mealItems[mealType] || [];
    const mealResponse = items.reduce((acc, item) => {
      acc[item.key] = String(item.quantity);
      return acc;
    }, {});

    const payload = {
      programChallengeId: Number(challengeId),
      mealType,
      mealResponse,
      mealDate: viewDate,
      runnerId: runnerDetails?.runnerId,
    };
    let response = postChallengeMealLog(payload);

    response
      .then((res) => {
        Snackbar.show({
          variant: "success",
          isAutoHide: true,
          preventDuplicate: true,
          message:
            res.data?.success?.verbose || "Meal log submitted successfully!",
        });
        initialLoad(challengeId, viewDate);
        setMealDialogs((prev) => ({ ...prev, [mealType]: false }));
      })
      .catch((error) => {
        Snackbar.show({
          variant: "error",
          isAutoHide: true,
          preventDuplicate: true,
          message:
            error.data?.error?.verbose || Properties.Something_went_wrong,
        });
        console.error("❌ Error submitting meal log:", error);
      });
  };

  return (
    <div className={classes.container} style={style}>
      <div className="dietSection">
        <span className="title">Meal Plan</span>&nbsp;
        <div className={classes.buttonAlignment}>
          {Object.keys(mealItems).map((mealType) => (
            <Button
              key={mealType}
              color="primary"
              variant="contained"
              className={classes.mealBtnStyle}
              onClick={() =>
                setMealDialogs((prev) => ({ ...prev, [mealType]: true }))
              }
            >
              <img
                src={
                  mealType === "Breakfast"
                    ? breakfastIcon
                    : mealType === "Lunch"
                    ? lunchIcon
                    : mealType === "Dinner"
                    ? dinnerIcon
                    : miniMeal
                }
                alt={`${mealType} icon`}
                className={classes.iconStyle}
              />
              {mealType}
            </Button>
          ))}
        </div>
      </div>

      {/* 🔹 Render Dialogs Dynamically */}
      {Object.entries(mealItems).map(([mealType, items]) => (
        <Dialog
          key={mealType}
          open={mealDialogs[mealType] || false}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setMealDialogs((prev) => ({ ...prev, [mealType]: false }));
            }
          }}
          disableEscapeKeyDown
          maxWidth="xs"
          fullWidth
          PaperProps={{ style: { borderRadius: 12, padding: 10 } }}
        >
          <DialogContent>
            <Typography
              variant="h6"
              style={{
                fontWeight: 600,
                marginBottom: 10,
                textAlign: "center",
              }}
            >
              {mealType}
            </Typography>

            {items.length > 0 ? (
              items.map((item, index) => (
                <Box
                  key={item.key}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    style={{ fontWeight: 500, textTransform: "capitalize" }}
                  >
                    {item.name}
                  </Typography>
                  <Box className={classes.container}>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(mealType, index, -1)}
                    >
                      –
                    </IconButton>
                    <Typography>{item.quantity}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(mealType, index, 1)}
                    >
                      +
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography align="center" color="textSecondary">
                No data available
              </Typography>
            )}
          </DialogContent>

          <DialogActions className={classes.buttonAlignmentDialogBox}>
            <Button
              variant="contained"
              onClick={() =>
                setMealDialogs((prev) => ({ ...prev, [mealType]: false }))
              }
              className={classes.btnStyle}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.btnStyle}
              onClick={() => handleSubmit(mealType)}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      ))}
    </div>
  );
}

const styles = (theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    borderRadius: "10px",
    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
    padding: "10px",
    [theme.breakpoints.down("sm")]: {
      gap: "10px",
    },
    "& .title": {
      fontWeight: 600,
      fontSize: "22px",
      minWidth: "fit-content",
      [theme.breakpoints.down("sm")]: {
        fontSize: "17px",
      },
    },
    "& .dietSection": {
      color: "black",
      display: "flex",
      flexDirection: "column", // stack items vertically
      alignItems: "flex-start", // optional: aligns items to the start
      [theme.breakpoints.down("xs")]: {
        gap: "0px",
      },
    },
    "& .dietSection p": {
      margin: "4px 0", // reduce margin between lines
    },
  },
  buttonAlignment: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: "10px",
    gap: "10px",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  buttonAlignmentDialogBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },
  btnStyle: {
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#296DFA",
    },
    width: "150px",
    alignContent: "center",
    padding: "6px 16px",
    maxWidth: "300px",
    height: "38px",
    borderRadius: "10px",
    backgroundColor: "#09456C",
    color: "#fff",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      width: "50%",
    },
  },
  mealBtnStyle: {
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f0bc7dff",
    },
    width: "150px",
    alignContent: "center",
    justifyContent:"center",
    padding: "6px 16px",
    maxWidth: "300px",
    height: "38px",
    borderRadius: "10px",
    backgroundColor: "#FFBA66",
    color: "#09456C",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      width: "45%",
    },
  },
  iconStyle: {
    display: "flex",
    justifyContent:"space-around",
    alignItems:"center",
    width: "20px",
    height: "20px",
    marginRight: "10px",
  }
});

export default compose(withStyles(styles), withRouter)(MealResponseComponent);
